// =============================================================================
// PAGES/EXTENSOES.TSX — Integrações e Sistema › Extensões (grid de cards)
// =============================================================================
// Lista as extensões em cards (padrão hub-laravel-extensoes-e-tokens §7). Clicar
// num card abre um MODAL LATERAL (offcanvas à direita) com download, instruções de
// instalação e o token (revelar 1x — o admin gera; o admin nunca vê o token).
// A administração dos tokens de todos os usuários fica em tela própria (admin).
// =============================================================================
import { useEffect, useState, useCallback } from 'react';
import { Puzzle, Download, KeyRound, Copy, Loader2, CheckCircle2, AlertTriangle, ShieldCheck, X, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { extensaoApi, type ExtensaoInfo, type ExtensaoStatus } from '../services/api';

function fmtData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
}

export default function Extensoes() {
  const [info, setInfo] = useState<ExtensaoInfo | null>(null);
  const [status, setStatus] = useState<ExtensaoStatus | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aberta, setAberta] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [i, s] = await Promise.all([extensaoApi.info(), extensaoApi.status()]);
      setInfo(i); setStatus(s);
    } catch { toast.error('Falha ao carregar as extensões.'); }
    finally { setCarregando(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const chip = statusChip(status);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-1"><Puzzle size={22} className="text-otm-orange" /><h1 className="text-xl font-bold text-otm-txt-1">Extensões</h1></div>
      <p className="text-sm text-otm-txt-3 mb-5">Ferramentas de navegador que conectam o CRM a sistemas externos pela sua própria sessão. Clique numa extensão para baixar e configurar.</p>

      {carregando && !info ? (
        <div className="flex items-center gap-2 text-otm-txt-3 py-10 justify-center"><Loader2 size={18} className="animate-spin" /> Carregando…</div>
      ) : info ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button onClick={() => setAberta(true)} className="text-left bg-white rounded-2xl border border-otm-border shadow-sm hover:shadow-md hover:border-otm-orange/40 transition-all p-5 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-2">
              <div className="w-11 h-11 rounded-xl bg-otm-orange/10 flex items-center justify-center text-otm-orange shrink-0"><Puzzle size={22} /></div>
              <span className="text-[11px] font-semibold text-otm-txt-3 bg-otm-subtle rounded-full px-2 py-0.5">v{info.versao}</span>
            </div>
            <div>
              <h2 className="text-base font-bold text-otm-txt-1">{info.nome}</h2>
              <p className="text-sm text-otm-txt-3 mt-1 line-clamp-3">{info.descricao}</p>
            </div>
            <div className="flex items-center gap-2 mt-auto pt-1">
              <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${chip.cls}`}>{chip.icon}{chip.txt}</span>
              <span className="text-[11px] text-otm-txt-3 ml-auto inline-flex items-center gap-1"><Globe size={13} /> Chrome · Edge</span>
            </div>
          </button>
        </div>
      ) : null}

      {info && (
        <OffcanvasExtensao
          aberta={aberta}
          onFechar={() => setAberta(false)}
          info={info}
          status={status}
          aoRevelar={carregar}
        />
      )}
    </div>
  );
}

function statusChip(s: ExtensaoStatus | null): { txt: string; cls: string; icon: React.ReactNode } {
  if (!s) return { txt: '—', cls: 'bg-otm-subtle text-otm-txt-3', icon: null };
  if (s.conectada) return { txt: `Conectada v${s.ultimaVersao ?? ''}`, cls: 'bg-green-100 text-green-700', icon: <CheckCircle2 size={12} /> };
  if (s.aguardandoRevelacao) return { txt: 'Token pronto · revelar', cls: 'bg-amber-100 text-amber-800', icon: <KeyRound size={12} /> };
  if (s.temToken) return { txt: 'Configurar', cls: 'bg-otm-subtle text-otm-txt-2', icon: null };
  return { txt: 'Sem token', cls: 'bg-otm-subtle text-otm-txt-3', icon: null };
}

// ── Offcanvas lateral (direita) com download + instruções + token ─────────────
function OffcanvasExtensao({ aberta, onFechar, info, status, aoRevelar }: {
  aberta: boolean; onFechar: () => void; info: ExtensaoInfo; status: ExtensaoStatus | null; aoRevelar: () => void;
}) {
  const [tokenRevelado, setTokenRevelado] = useState<string | null>(null);
  const [revelando, setRevelando] = useState(false);
  const zipUrl = `/extensoes/${info.slug}-${info.versao}.zip`;

  useEffect(() => { if (!aberta) setTokenRevelado(null); }, [aberta]);

  async function revelar() {
    setRevelando(true);
    try {
      const r = await extensaoApi.revelarToken();
      setTokenRevelado(r.token);
      toast.success('Token revelado. Copie agora — ele não aparece de novo.');
      aoRevelar();
    } catch (e) {
      const msg = (e as { response?: { data?: { mensagem?: string } } })?.response?.data?.mensagem;
      toast.error(msg ?? 'Não foi possível revelar o token.');
    } finally { setRevelando(false); }
  }
  function copiar(v: string) { navigator.clipboard.writeText(v).then(() => toast.success('Copiado.'), () => toast.error('Não foi possível copiar.')); }

  return (
    <>
      {/* backdrop */}
      <div className={`fixed inset-0 bg-black/40 z-[80] transition-opacity ${aberta ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onFechar} />
      {/* painel */}
      <aside className={`fixed inset-y-0 right-0 z-[81] w-full max-w-md bg-white shadow-xl flex flex-col transition-transform duration-250 ${aberta ? 'translate-x-0' : 'translate-x-full'}`} role="dialog" aria-label={info.nome}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-otm-border">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-otm-orange/10 flex items-center justify-center text-otm-orange shrink-0"><Puzzle size={18} /></div>
            <div className="min-w-0"><h2 className="text-sm font-bold text-otm-txt-1 truncate">{info.nome}</h2><span className="text-[11px] text-otm-txt-3">v{info.versao}</span></div>
          </div>
          <button onClick={onFechar} className="text-otm-txt-3 hover:text-otm-txt-1"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <p className="text-sm text-otm-txt-3">{info.descricao}</p>

          <a href={zipUrl} download className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-otm-orange px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"><Download size={16} /> Baixar extensão (.zip)</a>

          {status?.versaoDesatualizada && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"><AlertTriangle size={16} className="mt-0.5 shrink-0" /> A extensão instalada ({status.ultimaVersao}) está desatualizada. Baixe e reinstale a v{status.versaoVigente}.</div>
          )}

          {/* Token */}
          <section>
            <h3 className="text-sm font-bold text-otm-txt-1 flex items-center gap-2 mb-2"><KeyRound size={16} className="text-otm-txt-3" /> Token de conexão</h3>
            {tokenRevelado ? (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-xs font-semibold text-green-800 mb-1 flex items-center gap-1"><ShieldCheck size={13} /> Copie agora (não aparece de novo):</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border border-green-200 rounded px-2 py-1.5 font-mono break-all">{tokenRevelado}</code>
                  <button onClick={() => copiar(tokenRevelado)} className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-green-300 bg-white px-2.5 py-1.5 text-xs text-green-800 hover:bg-green-100"><Copy size={13} /> Copiar</button>
                </div>
              </div>
            ) : status?.aguardandoRevelacao ? (
              <>
                <p className="text-sm text-otm-txt-3 mb-3">O administrador gerou um token para você. Revele-o <strong>uma única vez</strong> e cole na extensão.</p>
                <button onClick={revelar} disabled={revelando} className="inline-flex items-center gap-1.5 rounded-lg bg-otm-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40">{revelando ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Revelar token</button>
              </>
            ) : status?.temToken ? (
              <p className="text-sm text-otm-txt-2">Token já revelado em <strong>{fmtData(status.reveladoEm)}</strong>{status.prefixo ? <> (<code className="font-mono">{status.prefixo}</code>)</> : null}. Se o perdeu, peça ao administrador um novo.</p>
            ) : (
              <p className="text-sm text-otm-txt-2">Você ainda não tem um token. Peça ao administrador para gerar o seu.</p>
            )}
          </section>

          {/* Instruções */}
          <section>
            <h3 className="text-sm font-bold text-otm-txt-1 mb-2">Instalar (Chrome / Edge)</h3>
            <ol className="space-y-1.5 text-sm text-otm-txt-2 list-decimal pl-5">
              <li><strong>Baixe</strong> e <strong>extraia</strong> numa pasta fixa (ex.: <code className="font-mono text-xs bg-otm-subtle px-1 py-0.5 rounded">C:\OtimizCred\Extensoes\</code>). Confira <code className="font-mono text-xs bg-otm-subtle px-1 py-0.5 rounded">…\{info.slug}\manifest.json</code>. Não mova a pasta depois.</li>
              <li>Abra <code className="font-mono text-xs bg-otm-subtle px-1 py-0.5 rounded">chrome://extensions</code> (ou <code className="font-mono text-xs bg-otm-subtle px-1 py-0.5 rounded">edge://extensions</code>) → ative o <strong>Modo do desenvolvedor</strong>.</li>
              <li><strong>Carregar sem compactação</strong> → selecione a pasta <code className="font-mono text-xs bg-otm-subtle px-1 py-0.5 rounded">{info.slug}</code> → fixe (📌).</li>
              <li>No ícone da extensão → <strong>opções</strong>: escolha o <strong>Ambiente</strong>, cole o <strong>token</strong> e salve.</li>
              <li>Logado no iSolve no mesmo navegador, use a <strong>gaveta na lateral direita</strong> das telas do CRM (ícone) → <strong>Conectar iSolve agora</strong>.</li>
            </ol>
            <p className="text-xs text-otm-txt-3 mt-2">Depois, a extensão revalida sozinha enquanto você estiver logado no iSolve. Nenhuma senha do QuintoAndar é guardada.</p>
          </section>
        </div>
      </aside>
    </>
  );
}
